import { AsyncPipe, NgClass, NgIf } from '@angular/common';
import { Component, Input, OnDestroy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { map, Observable, Subject, take } from 'rxjs';

import { CdwSearchGridComponentStore } from '../+component-state/cdw-search-grid-component.store';

@Component({
  selector: 'lib-select-all-banner',
  templateUrl: './select-all-banner.component.html',
  styleUrls: ['./select-all-banner.component.scss'],
  standalone: true,
  imports: [AsyncPipe, NgClass, NgIf, MatButtonModule, MatIconModule],
})
export class SelectAllBannerComponent<T extends { id: string }>
  implements OnDestroy
{
  /**
   * Optional callback to fetch all IDs for "select all" functionality
   * When provided, clicking "Select all X items" will call this function
   * to get all IDs matching current filters, then add them to selectedIds
   */
  @Input() fetchAllIds?: () => Observable<string[]>;

  /**
   * Font size for the banner text
   * Defaults to 'text-base' (16px)
   */
  @Input() fontSize = 'text-base';

  public promptSelectAll$: Observable<boolean>;

  public selectAll$: Observable<boolean>;

  public total$: Observable<number | null>;

  public idsSelectedOnPage$: Observable<number>;

  private onDestroy$: Subject<void> = new Subject();

  constructor(private readonly componentState: CdwSearchGridComponentStore<T>) {
    this.promptSelectAll$ = this.componentState.promptSelectAll$;
    this.selectAll$ = this.componentState.selectAll$;
    this.idsSelectedOnPage$ = this.componentState.idsSelectedOnPage$;
    this.total$ = this.componentState.total$;
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next();
  }

  public close(): void {
    this.componentState.turnOffPromptSelectAll();
  }

  public turnOnSelectAll(): void {
    if (this.fetchAllIds) {
      // Fetch all IDs from backend, then add them to selectedIds
      this.fetchAllIds()
        .pipe(take(1))
        .subscribe((allIds: string[]): void => {
          this.componentState.turnOnSelectAllWithIds({ allIds });
          this.componentState.updateMultiSelectCheckboxState();
        });
    } else {
      // Fallback to old behavior (only selects current page)
      this.componentState.turnOnSelectAll();
      this.componentState.updateMultiSelectCheckboxState();
    }
  }

  public turnOffSelectAll(): void {
    this.componentState.turnOffSelectAll();
    this.componentState.turnOffPromptSelectAll();
    this.componentState.clearSelectedIds();
    this.componentState.turnOffFilterBySelected();
    this.componentState.updateMultiSelectCheckboxState();
  }

  public getTotal(): Observable<string> {
    return this.total$.pipe(
      map((total: number | null): string => (total ? `${total} ` : ''))
    );
  }
}
